import { useEffect, useRef } from "react";
import { TURNSTILE_SITE_KEY } from "./api";

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "error-callback": () => void;
          "expired-callback": () => void;
        },
      ) => string;
      remove: (id: string) => void;
    };
  }
}

export function Turnstile({ onToken }: { onToken: (token: string) => void }) {
  const element = useRef<HTMLDivElement>(null);
  const widget = useRef("");
  const siteKey = TURNSTILE_SITE_KEY;
  useEffect(() => {
    if (!siteKey || !element.current) return;
    let cancelled = false;
    let retry: number | undefined;
    let script = document.querySelector<HTMLScriptElement>(
      "script[data-hb-turnstile]",
    );
    const clearToken = () => onToken("");
    const render = () => {
      if (cancelled || widget.current) return;
      if (!window.turnstile || !element.current) {
        retry = window.setTimeout(render, 200);
        return;
      }
      widget.current = window.turnstile.render(element.current, {
        sitekey: siteKey,
        callback: onToken,
        "error-callback": clearToken,
        "expired-callback": clearToken,
      });
    };
    if (!script) {
      script = document.createElement("script");
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.hbTurnstile = "true";
      document.head.appendChild(script);
    }
    script.addEventListener("load", render);
    render();
    return () => {
      cancelled = true;
      if (retry) window.clearTimeout(retry);
      script?.removeEventListener("load", render);
      if (widget.current) window.turnstile?.remove(widget.current);
      widget.current = "";
    };
  }, [siteKey, onToken]);
  return siteKey ? <div ref={element} className="turnstile" /> : null;
}
